import { useAppSelector } from "@/app/hooks";

/**
 * The permission catalogue, mirroring `apps/api/src/auth/permissions.ts`.
 *
 * These strings must match the backend exactly — they are the same vocabulary
 * used by the seeded roles and every API policy. A typo here does not fail
 * loudly; it silently hides a button from someone who should see it.
 *
 * Shape: `<module>.<action>`, where action is `view` / `create` / `edit` /
 * `delete` plus a few one-off verbs (`ban`, `publish`, `refund`, `send`,
 * `resolve`, `export`) whose blast radius is different enough from ordinary
 * editing to deserve their own grant. The rights matrix the panel renders is
 * served by the API, not built from this list — this exists only for the
 * `usePermission()` gate.
 *
 * **This gate is convenience, not security.** It stops the panel offering
 * actions that will be rejected. The server re-checks every single call, and
 * it is the server's answer that matters.
 */
export const PERMISSIONS = {
  // Overview
  DASHBOARD_VIEW: "dashboard.view",

  // Players
  PLAYER_VIEW: "player.view",
  PLAYER_EDIT: "player.edit",
  PLAYER_DELETE: "player.delete",
  PLAYER_BAN: "player.ban",
  PLAYER_UNBAN: "player.unban",

  // Characters
  CHARACTER_VIEW: "character.view",
  CHARACTER_CREATE: "character.create",
  CHARACTER_EDIT: "character.edit",
  CHARACTER_DELETE: "character.delete",

  // Balance & config releases
  BALANCE_VIEW: "balance.view",
  BALANCE_EDIT: "balance.edit",
  CONFIG_PUBLISH: "config.publish",
  CONFIG_ROLLBACK: "config.rollback",

  // Missions
  MISSION_VIEW: "mission.view",
  MISSION_CREATE: "mission.create",
  MISSION_EDIT: "mission.edit",
  MISSION_DELETE: "mission.delete",

  // Rewards
  REWARD_VIEW: "reward.view",
  REWARD_CREATE: "reward.create",
  REWARD_EDIT: "reward.edit",
  REWARD_DELETE: "reward.delete",

  // Progression curves
  PROGRESSION_VIEW: "progression.view",
  PROGRESSION_EDIT: "progression.edit",

  // Matches
  MATCH_VIEW: "match.view",

  // Matchmaking
  MATCHMAKING_VIEW: "matchmaking.view",
  MATCHMAKING_EDIT: "matchmaking.edit",

  // Quarantine
  QUARANTINE_VIEW: "quarantine.view",
  QUARANTINE_RESOLVE: "quarantine.resolve",

  // Anti-cheat
  SECURITY_VIEW: "security.view",
  SECURITY_EDIT: "security.edit",

  // Tournaments
  TOURNAMENT_VIEW: "tournament.view",
  TOURNAMENT_CREATE: "tournament.create",
  TOURNAMENT_EDIT: "tournament.edit",
  TOURNAMENT_DELETE: "tournament.delete",

  // Ranking
  RANKING_VIEW: "ranking.view",
  RANKING_EDIT: "ranking.edit",

  // Leaderboards
  LEADERBOARD_VIEW: "leaderboard.view",
  LEADERBOARD_EDIT: "leaderboard.edit",
  LEADERBOARD_DELETE: "leaderboard.delete",

  // Economy
  ECONOMY_VIEW: "economy.view",
  /** Bounded by `support_grant_cap_coins` in the published config. */
  ECONOMY_GRANT_CAPPED: "economy.grant.capped",
  ECONOMY_GRANT_UNCAPPED: "economy.grant.uncapped",
  ECONOMY_REVOKE: "economy.revoke",

  // Store
  STORE_VIEW: "store.view",
  STORE_CREATE: "store.create",
  STORE_EDIT: "store.edit",
  STORE_DELETE: "store.delete",

  // Purchases
  PURCHASE_VIEW: "purchase.view",
  PURCHASE_REFUND: "purchase.refund",

  // Announcements
  ANNOUNCEMENT_VIEW: "announcement.view",
  ANNOUNCEMENT_CREATE: "announcement.create",
  ANNOUNCEMENT_EDIT: "announcement.edit",
  ANNOUNCEMENT_DELETE: "announcement.delete",
  /** Publish, unpublish, and edit or archive what is already published. */
  ANNOUNCEMENT_PUBLISH: "announcement.publish",

  // Notifications
  NOTIFICATION_VIEW: "notification.view",
  NOTIFICATION_SEND: "notification.send",
  /**
   * Writing into colleagues' panel inboxes. Separate from `notification.send`
   * because a message inside the panel carries the panel's authority.
   */
  ADMIN_NOTICE_SEND: "admin.notice.send",

  // Analytics
  ANALYTICS_VIEW: "analytics.view",
  REPORT_EXPORT: "report.export",

  // Governance
  AUDIT_VIEW: "audit.view",

  ADMIN_VIEW: "admin.view",
  ADMIN_CREATE: "admin.create",
  ADMIN_EDIT: "admin.edit",
  ADMIN_DELETE: "admin.delete",
  /** Setting another admin's password. Never your own — that is `/me/password`. */
  ADMIN_PASSWORD_RESET: "admin.password.reset",

  ROLE_VIEW: "role.view",
  ROLE_CREATE: "role.create",
  ROLE_EDIT: "role.edit",
  ROLE_DELETE: "role.delete",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Managing admins and roles, and reading the activity log, are the account
 * owner's alone.
 *
 * These rights still exist on the API, but they are not handed to sub-admins:
 * the Admins & roles section is shown only to the owner, and the role editor
 * neither offers these rights nor saves them onto a role. Letting a sub-admin
 * edit roles is letting them grant themselves anything; and the API already
 * answers every sub-admin `403 OWNER_ONLY` on the activity log, so
 * `audit.view` on a role only ever promised something that did not work.
 */
export const OWNER_ONLY_PERMISSIONS: ReadonlySet<string> = new Set([
  PERMISSIONS.AUDIT_VIEW,
  PERMISSIONS.ADMIN_VIEW,
  PERMISSIONS.ADMIN_CREATE,
  PERMISSIONS.ADMIN_EDIT,
  PERMISSIONS.ADMIN_DELETE,
  PERMISSIONS.ADMIN_PASSWORD_RESET,
  PERMISSIONS.ROLE_VIEW,
  PERMISSIONS.ROLE_CREATE,
  PERMISSIONS.ROLE_EDIT,
  PERMISSIONS.ROLE_DELETE,
]);

export function isOwnerOnlyPermission(permission: string): boolean {
  return OWNER_ONLY_PERMISSIONS.has(permission);
}

/**
 * Any one of these opens the announcements screen — the API lets every
 * announcement write right read the list, not only `announcement.view`.
 */
export const ANNOUNCEMENT_ACCESS: readonly Permission[] = [
  PERMISSIONS.ANNOUNCEMENT_VIEW,
  PERMISSIONS.ANNOUNCEMENT_CREATE,
  PERMISSIONS.ANNOUNCEMENT_EDIT,
  PERMISSIONS.ANNOUNCEMENT_DELETE,
  PERMISSIONS.ANNOUNCEMENT_PUBLISH,
];

/**
 * Exact match only, plus the `*` super-admin wildcard — identical semantics to
 * the server. No prefix matching: `economy.grant` must not imply
 * `economy.grant.uncapped`, or the panel offers an action the API will refuse
 * and the user gets a mystery 403.
 */
export function checkPermission(granted: readonly string[], permission: Permission): boolean {
  return granted.includes("*") || granted.includes(permission);
}

export function usePermission(permission: Permission): boolean {
  const granted = useAppSelector((state) => state.session.permissions);
  return checkPermission(granted, permission);
}

/** True when the user holds at least one of these. Useful for section gates. */
export function useAnyPermission(...permissions: Permission[]): boolean {
  const granted = useAppSelector((state) => state.session.permissions);
  return permissions.some((p) => checkPermission(granted, p));
}
